import React, { memo, useCallback } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import _memoize from 'lodash/memoize';
import { colors } from '@lifesize/ux-assets';
import { hooks, media } from '@lifesize/clients.sdk';
import { ReactComponent as CaptionIcon } from '@lifesize/ux-assets/vectors/svgIcons/caption.svg';
import { Fab, makeStyles, Tooltip } from '@material-ui/core';
import { FLAG_ENABLE_LIVE_CAPTIONS } from 'constants/FeatureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';

interface Props {
  intl: { formatMessage: Function };
}

const messages = defineMessages({
  enableCaptions: {
    id: 'components.incall.controls.liveCaption.tooltip.on',
    defaultMessage: 'Turn captions on'
  },
  disableCaptions: {
    id: 'components.incall.controls.liveCaption.tooltip.off',
    defaultMessage: 'Turn captions off'
  }
});

export const LiveCaptionsButton = (props: Props) => {
  const { intl } = props;
  const captionsEnabled = hooks.useCaptionsEnabledStatus();
  const liveCaptionsFlagEnabled = useFeatureFlag(FLAG_ENABLE_LIVE_CAPTIONS);
  const isConnected = hooks.useCallConnected();
  const inCall = hooks.useInCall();
  const isEscalated = hooks.useIsEscalated();
  const isDisabled = !inCall || !isConnected || !isEscalated;
  const classes = useStyles({ ...getStyleOverride(captionsEnabled) });
  const tooltipClasses = useStylesTooltip();

  const onClick = useCallback(() => {
    media.toggleCaptionsEnabled(!captionsEnabled);
  }, [captionsEnabled]);

  if (!liveCaptionsFlagEnabled) return null;

  return (
    <Tooltip
      arrow={true}
      classes={tooltipClasses}
      placement="top"
      title={intl.formatMessage(captionsEnabled ? messages.disableCaptions : messages.enableCaptions)}
    >
      <span data-testid="live-caption-btn">
        <Fab classes={classes} onClick={onClick} disabled={isDisabled} size="medium">
          <CaptionIcon data-testid="live-caption-icon" />
        </Fab>
      </span>
    </Tooltip>
  );
};

/** Style selectors that can be dynamically modified */
interface ButtonStyleProps {
  backgroundColor: string;
  borderColor: string;
  opacity: number;
}

const getStyleOverride = _memoize(
  (captionsEnabled: boolean): ButtonStyleProps => {
    return {
      backgroundColor: captionsEnabled ? colors.agileGreen : 'transparent',
      borderColor: captionsEnabled ? colors.agileGreen : colors.white,
      opacity: captionsEnabled ? 0.85 : 0.6
    };
  }
);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: (btnStyle: ButtonStyleProps) => btnStyle.backgroundColor,
    border: (btnStyle: ButtonStyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: colors.white,
    margin: 0,
    opacity: (btnStyle: ButtonStyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: (currentStyle: ButtonStyleProps) => currentStyle.backgroundColor,
      borderColor: (currentStyle: ButtonStyleProps) => currentStyle.borderColor,
      color: colors.white,
      opacity: 1.0
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: `${colors.squidInk} !important`,
    opacity: `1 !important`
  }
}));

const useStylesTooltip = makeStyles(() => ({
  popper: {
    textAlign: 'center'
  }
}));

export default memo(injectIntl(LiveCaptionsButton));
